import { Fab } from "@rmwc/fab";
import { Icon } from "@rmwc/icon";
import { List } from "@rmwc/list";
import { RESSOURCE_DISTRIBUTION_UTILISATEUR, RESSOURCE_SERVICES, RESSOURCE_TARIFS } from "Achat/API/AchatApi";
import { URL_BASE } from "App/Constants";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { selectDataReferentielServiceTypes, selectDataReferentielUniteTypes } from "Referentiel/Store/selectors";
import { GET } from "Request/Components/RequestUtils";
import { GET_LIST, GET_LIST_WITH_PARAMS, useJsonRequest } from "Request/Components/useJsonRequest";
import { selectDataResourceReceived } from "Request/Store/selectors";
import "./Distribution.css";
import { DistributionList } from "./DistributionList";

// AYB 2026.02.19 clé store dédiée + refetch après ticket pour affichage dernier service acheté
const STORE_SUFFIX_DISTRIBUTION = "distribution";

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher les distributions en cours
 * @category Distribution
 */
function Distribution(props) {
  const {
    borne
  } = props;
  //#region INITIALISATION
  // Initialisation du state
  const [data, setData] = useState(null);

  // Initialisation des références
  const countReqResult = useRef(0);  // Compteur du nombre de requêtes réalisées
  const currentDetailAchats = useRef([]); // Détail des achats du store
  const services = useRef([]);  // Services des achats du store
  const tarifs = useRef(null); // Tarifs des achats du store

  // Traduction i18n
  const { t } = useTranslation();

  // Initialisation des sélecteurs
  const dataReceivedDistribution = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_DISTRIBUTION_UTILISATEUR));
  const dataReceivedServices = useSelector((state) => selectDataResourceReceived(state, `${RESSOURCE_SERVICES}-${STORE_SUFFIX_DISTRIBUTION}`));
  const dataReceivedTarifs = useSelector((state) => selectDataResourceReceived(state, `${RESSOURCE_TARIFS}-${STORE_SUFFIX_DISTRIBUTION}`));
  const serviceTypes = useSelector((state) => selectDataReferentielServiceTypes(state));
  const uniteTypes = useSelector((state) => selectDataReferentielUniteTypes(state));

  const history = useHistory();
  const location = useLocation();
  //#endregion

  // #region REQUETES
  // Requête de la distribution
  const requestGetDistributionUtilisateur = useJsonRequest({
    command: GET,
    getMode: GET_LIST,
    resource: RESSOURCE_DISTRIBUTION_UTILISATEUR,
    url: URL_BASE,
  });

  // Requête de récupération des services
  const requestGetServices = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_SERVICES,
    url: URL_BASE,
  });

  // Requête de récupération des tarifs
  const requestGetTarifs = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_TARIFS,
    url: URL_BASE,
  });
  // #endregion

  // #region UTILS
  // Initialisation des données une fois toutes les requêtes reçues.
  const initializeData = useCallback(() => {
    if (countReqResult.current < 3) {
      return;
    }

    // Vérification de la disponibilité de toutes les données nécessaires
    if (!services.current || !serviceTypes.data || !tarifs.current || !uniteTypes.data) {
      return;
    }

    // Formatage des données
    let newData = currentDetailAchats.current;

    newData.forEach(detail => {
      // Récupération du service
      let service = services.current.find(serv => serv.idService === detail.idService);
      if (!service) {
        return;
      }

      // Récupération du serviceType
      let serviceType = serviceTypes.data.find(type => type.idServiceType === service.idServiceType);
      if (!serviceType) {
        return;
      }

      // Récupération du tarif
      let tarif = tarifs.current.find(tar => tar.idServiceType === serviceType.idServiceType && tar.idSite === service.idSite);
      if (!tarif) {
        return;
      }

      // Récupération du type d'unité
      let uniteType = uniteTypes.data.find(type => type.idUniteType === tarif.idUniteType);
      if (!uniteType) {
        return;
      }

      // Récupération des infos du terminal
      let terminal = borne.find((borneItem) => borneItem.id === detail.achat.idSite);
      if (!terminal) {
        return;
      }

      // Insertion des champs dans l'objet du service
      detail["famille"] = uniteType.famille;
      detail["libelleType"] = uniteType.libelleType;
      detail["idServiceType"] = serviceType.idServiceType;
      detail["idUniteType"] = uniteType.idUniteType;
      detail["secondes"] = tarif.secondes;
      detail["serviceNom"] = service.nom;
      detail["serviceTypeNom"] = serviceType.libelle;
      detail["terminalNom"] = terminal.name;
      detail["codeAcces"] = detail.achat.CodeAcces ?? null;
      detail["immatriculation"] = detail.achat.immatriculation ?? null;

      // Calcul du dépassement
      moment(detail.dateLimiteValidite).diff(moment()) > 0
        ? detail["depassement"] = 0
        : detail["depassement"] = moment().diff(moment(detail.dateLimiteValidite), "days") + 1;
    });

    // Remise à zéro du compteur après le traitement complet
    countReqResult.current = 0;

    // Regroupement des services par serviceType
    let groupedData = _.groupBy(newData, "idServiceType");
    let groupedArray = [];

    // Mise en forme du tableau des détail Achat par serviceType
    Object.keys(groupedData).forEach(key => groupedArray.push({
      id: key,
      name: groupedData[key][0].serviceTypeNom,
      detailAchats: groupedData[key]
    }));

    if (groupedArray.length > 0) {
      // Mise à jour du state
      setData(groupedArray);
    }


  }, [borne, serviceTypes, uniteTypes]);
  //#endregion

  // #region EVENTS
  const handleClickRachat = (service) => {
    // Récupération de la borne sélectionnée
    const borneSelected = borne.find((borneItem) => borneItem.id === service.achat.idSite);
    // sélection de la borne à utiliser
    history.push({
      pathname: "/achat",
      state: {
        borne: borneSelected,
        codeAcces: service.achat.CodeAcces,
        dateValidite: service.dateLimiteValidite,
        immatriculation: service.achat.immatriculation,
        depassement: service.depassement,
        rachat: true,
        rachatIdService: service.idService,
        servicesActifs: currentDetailAchats.current
      }
    });
  };

  // Refresh de la distrivution
  const handleRefreshDistribution = () => {
    requestGetDistributionUtilisateur();
  };
  // #endregion

  // #region HOOK D'EFFET
  // Requête de la distribution en cours de l'utilisateur
  useEffect(() => {
    requestGetDistributionUtilisateur();
  }, [requestGetDistributionUtilisateur]);


  useEffect(() => {
    if (!dataReceivedDistribution) {
      return;
    }

    // Sauvegarde des détails des achats reçus
    currentDetailAchats.current = dataReceivedDistribution;

    // Alimentation du compteur de requêtes
    countReqResult.current = countReqResult.current + 1;

    // Récupération de la liste des sites dans lesquels il y a de la distribution
    let paramsServices = [];
    let paramsSites = [];
    dataReceivedDistribution.forEach((item) => {
      paramsServices.push(item.idService);
      paramsSites.push(item.achat.idSite);
    });

    paramsServices = _.uniq(paramsServices);
    paramsSites = _.uniq(paramsSites);

    requestGetServices([`idService%5B%5D=${paramsServices.join("&idService%5B%5D=")}`], STORE_SUFFIX_DISTRIBUTION);
    requestGetTarifs([`idSite%5B%5D=${paramsSites.join("&idSite%5B%5D=")}`], STORE_SUFFIX_DISTRIBUTION);
  }, [dataReceivedDistribution, requestGetServices, requestGetTarifs]);

  // AYB 2026.02.19 refetch après arrivée depuis ticket (backend peut retarder le nouveau service)
  useEffect(() => {
    if (!location.state?.fromTicket) return;
    const t = setTimeout(() => {
      requestGetDistributionUtilisateur();
      history.replace("/", { selectedIndexTab: 1 });
    }, 2000);
    return () => clearTimeout(t);
  }, [location.state?.fromTicket, requestGetDistributionUtilisateur, history]);

  // Récupération des services
  useEffect(() => {
    if (!dataReceivedServices) {
      return;
    }

    // Sauvegarde des services reçus
    services.current = dataReceivedServices;

    // Alimentation du compteur de requêtes
    countReqResult.current = countReqResult.current + 1;

    // Initialisation du state
    initializeData();
  }, [initializeData, dataReceivedServices]);

  // Récupération des tarifs
  useEffect(() => {
    if (!dataReceivedTarifs) {
      return;
    }

    // Sauvegarde des tarifs recus
    tarifs.current = dataReceivedTarifs;

    // Alimentation du compteur de requêtes
    countReqResult.current = countReqResult.current + 1;

    // Initialisation du state
    initializeData();
  }, [initializeData, dataReceivedTarifs]);
  // #endregion

  // #region INTERFACE
  return (
    <div className="Distribution-Container">
      {!data ?
        <>
          <Icon icon="not_interested" />
          <div>{t("distribution.placeholder")}</div>
        </>
        :
        <>
          <div className="Distribution-ScrollbarContainer">
            <PerfectScrollbar>
              <List twoLine>
                {data.map((categorie, index) => {
                  return (
                    <DistributionList
                      handleRachat={handleClickRachat}
                      categorie={categorie}
                      key={index}
                    />
                  )
                })}
              </List>
            </PerfectScrollbar>
          </div>
          <Fab
            className="Distribution-RefreshButton"
            icon="autorenew"
            onClick={() => handleRefreshDistribution()}
            style={{
              position: 'fixed',
              bottom: '90px',
              right: '1rem',
              left: 'auto',
              top: 'auto',
              zIndex: 1000
            }}
          />
        </>
      }
    </div >
  );
  // #endregion
}

export { Distribution };

